import { skipToken, useSuspenseQueries } from '@tanstack/react-query'
import { useDeferredValue } from 'react'
import { trpc } from '@/main'
import { deriveProfiles, type ProfileRouteParams } from './use-root-search'
import type { RootSearch } from '@/routes/__root'

type ReportInput = Exclude<Parameters<typeof trpc.reports.getReport.queryOptions>[0], typeof skipToken>

// The ONLY search keys the server `getReport` consumes (mirror of `Profiles` in apps/shared). The
// report query key MUST be built from JUST these — never the whole search object. Spreading all of
// `search` (q, p, the *Sort params, pageType, …) into the input made `q` part of the query key, so
// the report refetched (running its loading bar) on every list-search keystroke even though its data
// doesn't depend on `q`. List/render-only params stay out of the key; the report is now stable.
const REPORT_INPUT_KEYS = [
  'first',
  'second',
  'selected',
  'type',
  'orgType',
  'teamId',
  'evaluation',
  'members',
  'name',
] as const satisfies readonly (keyof ReportInput)[]

function reportInput(profiles: { first: unknown; second: unknown }) {
  if (!profiles.first && !profiles.second) return skipToken
  const input = {} as ReportInput
  for (const key of REPORT_INPUT_KEYS) {
    const value = (profiles as Record<string, unknown>)[key]
    if (value !== undefined) (input as Record<string, unknown>)[key] = value
  }
  return input
}

/**
 * Which report-family surface a route is, for `deriveProfiles`. Every other route passes nothing —
 * their first/second profiles come from the path params (`$teamId`, `$jobId`, ...) or from search.
 *  - 'own'    — `/report`, the viewer's OWN profile (`{ type: 'applicant' }`)
 *  - 'market' — `/market`, the market-wide profile, so a bare `/market` URL loads a report
 */
export type ReportSurface = 'own' | 'market'

/**
 * The `getReport` query for a route, built from its `params` + `loaderDeps`. Returns `undefined` when
 * no profile is selected (a bare `/market`) — there is nothing to fetch and nothing to render.
 *
 * Belongs in a route's `context`, so the loader and the component hold the SAME options object:
 *
 *   context: ({ deps, params }) => ({ reportQueryOptions: reportQueryOptions(deps, params) }),
 *   loader:  ({ context }) => { context.queryClient.query(context.reportQueryOptions).catch(noop) },
 *   // component: Route.useRouteContext().reportQueryOptions
 *
 * The fragile part was never the `queryOptions` call — it was `deriveProfiles`, which needs the path
 * params AND the surface, and used to run in four places: each loader (surface as a positional
 * boolean), the report component, the board and the distribution panel (all three re-detecting the
 * surface at render through `useRootSearch`). Four derivations of one key, agreeing by inspection.
 */
export function reportQueryOptions(deps: ReportSearchDeps, params: ProfileRouteParams, surface?: ReportSurface) {
  const profiles = deriveProfiles(deps, params, surface === 'own', 'search', surface === 'market')
  const input = reportInput({ ...deps, ...profiles })
  return input === skipToken ? undefined : trpc.reports.getReport.queryOptions(input)
}

/** The options `reportQueryOptions` returns when a profile IS selected — what components consume. */
export type ReportQueryOptions = NonNullable<ReturnType<typeof reportQueryOptions>>

export type ReportSearchDeps = Pick<RootSearch, (typeof REPORT_INPUT_KEYS)[number]>

/**
 * `loaderDeps` picker for every report-family route: EXACTLY the search keys the report input consumes
 * (REPORT_INPUT_KEYS), so the router's deps memoization re-runs those loaders only when the report can
 * actually change — not on unrelated search churn (dialog-open ids, `q` keystrokes).
 *
 * It used to carry `o`/`od` too, for a second query per profile slot (`getTeam`, keyed with the list's
 * sort) that warmed the Source selector inside each ProfileCard. The report now carries that roster
 * itself (`ReportEvaluationSource`) — the server already had the team in hand for a team profile — so
 * the extra query, its route-context field, its loader loop and the sort keys are all gone.
 */
export function reportSearchDeps(search: ReportSearchDeps): ReportSearchDeps {
  return {
    first: search.first,
    second: search.second,
    selected: search.selected,
    type: search.type,
    orgType: search.orgType,
    teamId: search.teamId,
    evaluation: search.evaluation,
    members: search.members,
    name: search.name,
  }
}

/**
 * Reads the report a route declared on its `context` and returns the DATA — which is all a component
 * ever receives (AGENTS.md: options never cross a component boundary).
 *
 * Two things make this a hook rather than the Entry→Loaded component split it replaces:
 *
 *  - `useSuspenseQueries` takes an ARRAY, so "no profile selected" is an EMPTY array rather than a
 *    conditional hook call. That is what lets the guard live here instead of in a wrapper component
 *    every rendering route would have to hand its options to.
 *  - the options object is deferred, so a filter/type change keeps the previous report on screen while
 *    the new one loads (keep-previous UX) — and `isFetching` stays true through that transition, so the
 *    progress bar spans it.
 */
export function useReport(queryOptions: ReportQueryOptions | undefined) {
  const deferred = useDeferredValue(queryOptions)
  // Annotated: an inline `cond ? [x] : []` narrows to a tuple union that `useSuspenseQueries` can't read.
  const queries: ReportQueryOptions[] = deferred ? [deferred] : []
  const [result] = useSuspenseQueries({ queries })
  // The KEY, not the object identity. The owning route mints a fresh options object in `context()` on
  // every `loaderDeps` change, and the layouts that own the report also own search this report does not
  // key on — `panelQ` (the integrants/applicants panel's own search box) and the list's `o`/`od` sort.
  // Comparing identity made every keystroke in that panel a "transition": the progress bar flashed with
  // no refetch behind it, which is the churn `reportSearchDeps` already exists to keep out of the key.
  const isTransitioning = JSON.stringify(deferred?.queryKey) !== JSON.stringify(queryOptions?.queryKey)
  return {
    report: result?.data,
    isFetching: (result?.isFetching ?? false) || isTransitioning,
  }
}
